import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import abnAmroLogo from "@/assets/logos/abn-amro.png";
import obaLogo from "@/assets/logos/oba.png";
import impactHubLogo from "@/assets/logos/impact-hub.png";
import bLabLogo from "@/assets/logos/b-lab.png";
import ecochainLogo from "@/assets/logos/ecochain.png";

interface SocialProofProps {
  isEnterpriseExpanded: boolean;
  setIsEnterpriseExpanded: (value: boolean) => void;
}

export const SocialProof = ({ 
  isEnterpriseExpanded, 
  setIsEnterpriseExpanded 
}: SocialProofProps) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);

  const handleEnterpriseToggle = () => {
    setIsEnterpriseExpanded(!isEnterpriseExpanded);
    
    if (!isEnterpriseExpanded) {
      setTimeout(() => {
        const enterpriseSection = document.getElementById("enterprise-section");
        if (enterpriseSection) {
          enterpriseSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  const logos = [
    { src: abnAmroLogo, alt: "ABN AMRO" },
    { src: obaLogo, alt: "OBA" },
    { src: impactHubLogo, alt: "Impact Hub" },
    { src: bLabLogo, alt: "B Lab" },
    { src: ecochainLogo, alt: "Ecochain" },
  ];

  // Placeholder counter - will be connected to HubSpot in Phase 6
  useEffect(() => {
    // Simulate counting up from 0 to current count
    const targetCount = 0; // Will be replaced with actual HubSpot count
    const duration = 1000;
    const steps = 20;
    const increment = targetCount / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCount(Math.floor(increment * currentStep));
      } else {
        setCount(targetCount);
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-16 md:py-24 px-4 bg-muted/30" aria-labelledby="social-proof-heading">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 id="social-proof-heading" className="font-clash text-3xl xs:text-4xl md:text-5xl font-bold mb-8 text-foreground tracking-tight">
            {t("socialProof.headline")}
          </h2>

          <div className="mb-8" role="status" aria-live="polite">
            {count >= 54 && (
              <>
                <div className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-brand-teal to-brand-purple bg-clip-text text-transparent mb-4 animate-scale-in font-clash" aria-label={t("socialProof.counter", { count })}>
                  {count}
                </div>
                <p className="text-xl md:text-2xl text-muted-foreground">
                  {t("socialProof.counter", { count })}
                </p>
                <span className="sr-only">{count} professionals have joined the waitlist</span>
              </>
            )}
          </div>

          <div className="mt-12">
            <p className="text-sm text-muted-foreground mb-6">
              {t("socialProof.teamIntro")}
            </p>
            
            {/* Logo Carousel - 2 rows with brick wall pattern */}
            <div className="relative w-full overflow-hidden space-y-6">
              {/* Fade overlays on sides */}
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-muted/30 to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-muted/30 to-transparent z-10 pointer-events-none" />
              
              {/* First Row */}
              <div className="flex gap-8 animate-scroll">
                {/* First set - repeat logos 7 times */}
                {Array.from({ length: 7 }).map((_, setIndex) =>
                  logos.map((logo, i) => (
                    <div
                      key={`logo-row1-set1-${setIndex}-${i}`}
                      className="flex-shrink-0 w-48 h-32 bg-background rounded-lg flex items-center justify-center p-6"
                      aria-hidden="true"
                    >
                      <img 
                        src={logo.src} 
                        alt={logo.alt}
                        className="max-w-full max-h-full object-contain grayscale opacity-40"
                      />
                    </div>
                  ))
                )}
                {/* Duplicate set for seamless loop */}
                {Array.from({ length: 7 }).map((_, setIndex) =>
                  logos.map((logo, i) => (
                    <div
                      key={`logo-row1-set2-${setIndex}-${i}`}
                      className="flex-shrink-0 w-48 h-32 bg-background rounded-lg flex items-center justify-center p-6"
                      aria-hidden="true"
                    >
                      <img 
                        src={logo.src} 
                        alt={logo.alt}
                        className="max-w-full max-h-full object-contain grayscale opacity-40"
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Second Row - Offset for brick wall pattern */}
              <div className="flex gap-8 animate-scroll" style={{ marginLeft: '-4rem' }}>
                {/* First set - repeat logos 7 times */}
                {Array.from({ length: 7 }).map((_, setIndex) =>
                  logos.map((logo, i) => (
                    <div
                      key={`logo-row2-set1-${setIndex}-${i}`}
                      className="flex-shrink-0 w-48 h-32 bg-background rounded-lg flex items-center justify-center p-6"
                      aria-hidden="true"
                    >
                      <img 
                        src={logo.src} 
                        alt={logo.alt}
                        className="max-w-full max-h-full object-contain grayscale opacity-40"
                      />
                    </div>
                  ))
                )}
                {/* Duplicate set for seamless loop */}
                {Array.from({ length: 7 }).map((_, setIndex) =>
                  logos.map((logo, i) => (
                    <div
                      key={`logo-row2-set2-${setIndex}-${i}`}
                      className="flex-shrink-0 w-48 h-32 bg-background rounded-lg flex items-center justify-center p-6"
                      aria-hidden="true"
                    >
                      <img 
                        src={logo.src} 
                        alt={logo.alt}
                        className="max-w-full max-h-full object-contain grayscale opacity-40"
                      />
                    </div>
                  ))
                )}
            </div>
          </div>
        </div>

        {/* Enterprise Section Trigger */}
        <div className="mt-32 text-center">
          <button
            onClick={handleEnterpriseToggle}
            className="group inline-flex items-center gap-2 text-base text-muted-foreground hover:text-foreground transition-colors duration-300"
            aria-expanded={isEnterpriseExpanded}
            aria-controls="enterprise-section"
          >
            <span>{t("enterprise.trigger")}</span>
            <ChevronRight
              className={`w-5 h-5 transition-transform duration-300 ${
                isEnterpriseExpanded ? "rotate-90" : ""
              }`}
              style={{
                background: "linear-gradient(90deg, hsl(99, 98%, 40%), hsl(330, 100%, 60%), hsl(263, 52%, 52%))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            />
          </button>
        </div>
      </div>
    </section>
  );
};
